'use client';
import { ReactNode, useState } from 'react';
import {
  Button as KorButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  IConversationMemberOption,
  TConversationParticipantSource,
  useConversationContext,
} from '@ui/components';
import { CrewMemberPicker } from './crew-member-picker';

export interface IProps {
  onAddMembers: (members: TConversationParticipantSource) => void;
  children: ReactNode;
}

/** Names the picked members the way the selection card shows them: "Ann, Bob and 3 more". */
export const memberSelectionLabel = (members: IConversationMemberOption[]) => {
  const names = members.slice(0, 2).map((member) => member.display_name);
  const more = members.length - names.length;
  return more > 0 ? `${names.join(', ')} and ${more} more` : names.join(' and ');
};

export const MembersSelectionModal = ({ children, onAddMembers }: IProps) => {
  const state = useConversationContext();
  const [selections, setSelections] = useState<IConversationMemberOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const onAdd = () => {
    onAddMembers({
      type: 'member',
      fan_ids: selections.map((member) => member.fan_id),
      count: selections.length,
      label: memberSelectionLabel(selections),
    });
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <span>{children}</span>
      </DialogTrigger>
      <DialogContent className="bg-white p-6 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl">Add Members</DialogTitle>
        </DialogHeader>

        {isOpen && (
          <CrewMemberPicker
            search={state.searchCrewMembers}
            selected={selections}
            onChange={setSelections}
          />
        )}

        <DialogFooter className="justify-end">
          <KorButton type="outline" size="small" onClick={() => setSelections([])}>
            Clear
          </KorButton>
          <KorButton type="primary" size="small" disabled={selections.length === 0} onClick={onAdd}>
            Add Members
          </KorButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
